import { useEffect } from 'react';
import { ArrowLeftOutlined, DeleteOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { ExamFileKind, ExamModule } from '@titularis/api-client';
import { edicoesDoEnac, ESTADOS } from '@titularis/ui';
import { api } from '../../api';
import { CadastroAssistido } from '../../components/CadastroAssistido';
import { DistribuicaoDaProva } from '../../components/DistribuicaoDaProva';
import { SearchBoxCriavel } from '../../components/SearchBoxCriavel';
import { MODULO_LABEL, MODULOS, STATUS_PROVA_COR, STATUS_PROVA_LABEL } from '../../dominio';

interface FormularioDeProva {
  name: string;
  /** O concurso. Distinto do módulo, que é a fase dele (D-20). */
  scopeId: string;
  module: ExamModule;
  year: number;
  boardId: string;
  notaryOfficeId?: string;
  /** A UF do concurso. Vazia no ENAC, que é nacional. */
  state?: string;
  /** No ENAC, "ano.semestre" ("2025.1"); a edição é derivada. */
  anoEdicao?: string;
  entryMode?: string;
  variant?: string;
  internalNotes?: string;
}

/**
 * Cadastro e edição de prova.
 *
 * Os campos específicos aparecem conforme o módulo, como no escopo: número do
 * ENAC no módulo ENAC, cartório e modalidade de ingresso nos demais. Mostrar
 * todos sempre convidaria a preencher o que não se aplica.
 */
export function ProvaFormPage() {
  const { id } = useParams<{ id: string }>();
  const ehNova = !id || id === 'nova';
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * De onde a pessoa veio, para o voltar devolver a lista como ela estava.
   * Quem chegou por link direto não tem origem, e volta para a lista limpa.
   */
  const origem = (location.state as { origem?: string } | null)?.origem;
  const voltarParaLista = origem ? `/provas?${origem}` : '/provas';
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormularioDeProva>();

  const modulo = Form.useWatch('module', form);
  const ehEnac = modulo === 'enac';

  const prova = useQuery({
    queryKey: ['exam', id],
    queryFn: () => api.getExam(id!),
    enabled: !ehNova,
  });

  /*
   * Só concursos: Legislações é catálogo e não tem prova. O filtro vem da API
   * pelo `kind`, e não de um `if` sobre o slug aqui - a tela não deve conhecer
   * o nome do escopo que precisa esconder.
   */
  const concursos = useQuery({
    queryKey: ['scopes', 'contest'],
    queryFn: () => api.listScopes({ kind: 'contest' }),
  });

  useEffect(() => {
    if (!prova.data) return;

    /*
     * Só os campos do formulário. Jogar a resposta inteira aqui registrava
     * `id`, `status`, `boardName` e afins como campos, que voltavam no envio -
     * ruído no payload e, no caso do `status`, um caminho lateral para mudar a
     * situação da prova sem passar pela rota que exige permissão.
     */
    form.setFieldsValue({
      name: prova.data.name,
      scopeId: prova.data.scopeId,
      module: prova.data.module,
      year: prova.data.year,
      boardId: prova.data.boardId,
      notaryOfficeId: prova.data.notaryOfficeId ?? undefined,
      state: prova.data.state ?? undefined,
      anoEdicao:
        prova.data.module === 'enac' && prova.data.semester
          ? `${prova.data.year}.${prova.data.semester}`
          : undefined,
      entryMode: prova.data.entryMode ?? undefined,
      variant: prova.data.variant ?? undefined,
      internalNotes: prova.data.internalNotes ?? undefined,
    });
  }, [prova.data, form]);

  const salvar = useMutation({
    mutationFn: async (valores: FormularioDeProva) => {
      // No ENAC, ano e semestre saem do "Ano/Edição" (2025.1); o número do ENAC
      // deixou de existir. Campo vazio vai como `null` para limpar valor gravado.
      const ehEnac = valores.module === 'enac';
      const [anoEnac, semestreEnac] =
        ehEnac && valores.anoEdicao ? valores.anoEdicao.split('.') : [];

      const dados = {
        name: valores.name,
        scopeId: valores.scopeId,
        module: valores.module,
        year: ehEnac ? Number(anoEnac) : valores.year,
        semester: ehEnac ? Number(semestreEnac) : null,
        enacNumber: null,
        boardId: valores.boardId,
        notaryOfficeId: valores.notaryOfficeId ?? null,
        // O Enac é nacional: a UF sai junto com os campos de concurso estadual.
        state: ehEnac ? null : (valores.state ?? null),
        entryMode: valores.entryMode ?? null,
        variant: valores.variant ?? null,
        internalNotes: valores.internalNotes ?? null,
      };

      return ehNova ? api.createExam(dados) : api.updateExam(id!, dados);
    },
    onSuccess: async (salva) => {
      message.success(ehNova ? 'Prova cadastrada.' : 'Prova atualizada.');
      await queryClient.invalidateQueries({ queryKey: ['exams'] });
      await queryClient.invalidateQueries({ queryKey: ['exam', id] });
      if (ehNova) void navigate(`/provas/${salva.id}`, { replace: true, state: location.state });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const publicar = useMutation({
    mutationFn: (status: 'published' | 'archived' | 'draft') => api.setExamStatus(id!, status),
    onSuccess: async () => {
      message.success('Situação da prova atualizada.');
      await queryClient.invalidateQueries({ queryKey: ['exam', id] });
      await queryClient.invalidateQueries({ queryKey: ['exams'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const excluir = useMutation({
    mutationFn: () => api.deleteExam(id!),
    onSuccess: async (resultado) => {
      message.success(
        resultado.questoes > 0
          ? `Prova excluída, junto com ${resultado.questoes} questão(ões).`
          : 'Prova excluída.',
      );
      await queryClient.invalidateQueries({ queryKey: ['exams'] });
      void navigate(voltarParaLista);
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const enviarArquivo = useMutation({
    mutationFn: ({ kind, arquivo }: { kind: ExamFileKind; arquivo: File }) =>
      api.uploadExamFile(id!, kind, arquivo),
    onSuccess: async () => {
      message.success('Arquivo enviado.');
      await queryClient.invalidateQueries({ queryKey: ['exam', id] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const abrirArquivo = async (kind: ExamFileKind) => {
    try {
      const { url } = await api.examFileUrl(id!, kind);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (erro) {
      message.error((erro as Error).message);
    }
  };

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space orientation="vertical" size={0}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            style={{ padding: 0 }}
            onClick={() => void navigate(voltarParaLista)}
          >
            Provas
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {ehNova ? 'Cadastrar prova' : (prova.data?.name ?? 'Prova')}
          </Typography.Title>
          {!ehNova && prova.data?.createdBy ? (
            <Typography.Text type="secondary">
              Cadastrada por {prova.data.createdByName ?? 'Conta excluída'}
            </Typography.Text>
          ) : null}
        </Space>

        {!ehNova && prova.data ? (
          <Space>
            <Tag color={STATUS_PROVA_COR[prova.data.status]}>
              {STATUS_PROVA_LABEL[prova.data.status]}
            </Tag>
            <Button onClick={() => void navigate(`/questoes?examId=${prova.data.id}`)}>
              Ver questões
            </Button>
            <Popconfirm
              title="Excluir a prova?"
              description="Somem também as questões desta prova e os PDFs enviados. Não tem volta."
              okText="Excluir"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={() => excluir.mutate()}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={excluir.isPending}
                aria-label="Excluir prova"
              >
                Excluir
              </Button>
            </Popconfirm>
            {prova.data.status === 'published' ? (
              <Popconfirm
                title="Arquivar a prova?"
                description="Ela deixa de aparecer para os alunos."
                okText="Arquivar"
                cancelText="Cancelar"
                onConfirm={() => publicar.mutate('archived')}
              >
                <Button loading={publicar.isPending}>Arquivar</Button>
              </Popconfirm>
            ) : (
              <Button
                type="primary"
                loading={publicar.isPending}
                onClick={() => publicar.mutate('published')}
              >
                Publicar prova
              </Button>
            )}
          </Space>
        ) : null}
      </Space>

      {prova.error ? <Alert type="error" showIcon title={(prova.error as Error).message} /> : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card size="small" title="Dados da prova" loading={!ehNova && prova.isPending}>
            <Form<FormularioDeProva>
              form={form}
              layout="vertical"
              initialValues={{ module: 'objective', year: new Date().getFullYear() }}
              onFinish={(valores) => salvar.mutate(valores)}
            >
              <Form.Item
                name="name"
                label="Nome da prova"
                rules={[{ required: true, message: 'Informe o nome da prova.' }]}
              >
                <Input placeholder="ENAC 2024 - 1ª aplicação" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="scopeId"
                    label="Concurso"
                    tooltip="O concurso a que a prova pertence. O módulo abaixo é a fase dele."
                    rules={[{ required: true, message: 'Escolha o concurso.' }]}
                  >
                    <Select
                      loading={concursos.isPending}
                      placeholder="Cartórios, Magistratura, OAB..."
                      options={(concursos.data ?? []).map((escopo) => ({
                        value: escopo.id,
                        label: escopo.name,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="module" label="Módulo" rules={[{ required: true }]}>
                    <Select
                      options={MODULOS.map((valor) => ({
                        value: valor,
                        label: MODULO_LABEL[valor],
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  {ehEnac ? (
                    <Form.Item
                      name="anoEdicao"
                      label="Ano/Edição"
                      rules={[{ required: true, message: 'Escolha o ano e a edição do ENAC.' }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="2025.1 - 1ª Edição"
                        options={edicoesDoEnac().map(({ value, label }) => ({ value, label }))}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="year"
                      label="Ano"
                      rules={[{ required: true, message: 'Informe o ano.' }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={1990} max={2100} />
                    </Form.Item>
                  )}
                </Col>
              </Row>

              <Form.Item
                name="boardId"
                label="Banca"
                rules={[{ required: true, message: 'Informe a banca.' }]}
              >
                <SearchBoxCriavel
                  queryKey={['boards']}
                  placeholder="Buscar banca"
                  rotulo="Banca"
                  rotuloAtual={prova.data?.boardName}
                  buscar={async (termo) => (await api.listBoards(termo)).items}
                  criar={(nome) => api.createBoard(nome)}
                />
              </Form.Item>

              {modulo === 'enac' ? null : (
                <Row gutter={16}>
                  <Col xs={24} md={6}>
                    {/*
                      A UF é campo próprio, e não o estado do cartório: um
                      concurso estadual abre dezenas de serventias de uma vez, e
                      em Provas para Imprimir é por ela que se procura.
                    */}
                    <Form.Item name="state" label="Estado">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="UF"
                        options={ESTADOS.map((estado) => ({
                          value: estado.uf,
                          label: `${estado.uf} - ${estado.nome}`,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={10}>
                    <Form.Item name="notaryOfficeId" label="Cartório">
                      <SearchBoxCriavel
                        queryKey={['notary-offices']}
                        placeholder="Buscar cartório"
                        rotulo="Cartório"
                        rotuloAtual={prova.data?.notaryOfficeName}
                        buscar={async (termo) => (await api.listNotaryOffices(termo)).items}
                        criar={(nome) => api.createNotaryOffice(nome)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="entryMode" label="Modalidade de ingresso">
                      <Input placeholder="Provimento, remoção..." />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Form.Item name="variant" label="Tipo ou versão da prova">
                <Input placeholder="1, 2, A, B, Rosa, Azul..." />
              </Form.Item>

              <Form.Item name="internalNotes" label="Observações internas">
                <Input.TextArea
                  rows={3}
                  placeholder="Notas para a equipe. Não aparecem para o aluno."
                />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={salvar.isPending}>
                {ehNova ? 'Cadastrar prova' : 'Salvar alterações'}
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card size="small" title="Arquivos">
            {ehNova ? (
              <Typography.Text type="secondary">
                Cadastre a prova para anexar os PDFs.
              </Typography.Text>
            ) : (
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                <ArquivoDaProva
                  titulo="PDF da prova"
                  kind="exam"
                  caminho={prova.data?.examFilePath ?? null}
                  enviando={enviarArquivo.isPending}
                  onEnviar={(arquivo) => enviarArquivo.mutate({ kind: 'exam', arquivo })}
                  onAbrir={() => void abrirArquivo('exam')}
                />
                <ArquivoDaProva
                  titulo="PDF do gabarito ou padrão de resposta"
                  kind="answer-key"
                  caminho={prova.data?.answerKeyFilePath ?? null}
                  enviando={enviarArquivo.isPending}
                  onEnviar={(arquivo) => enviarArquivo.mutate({ kind: 'answer-key', arquivo })}
                  onAbrir={() => void abrirArquivo('answer-key')}
                />
                {/*
                  O edital é do certame, e não da prova - mas mora aqui porque
                  certame não é entidade neste desenho, e o que se distribui é
                  sempre "os arquivos daquela prova". Num concurso com duas
                  fases, o mesmo edital sobe nas duas.
                */}
                <ArquivoDaProva
                  titulo="PDF do edital"
                  kind="edital"
                  caminho={prova.data?.editalFilePath ?? null}
                  enviando={enviarArquivo.isPending}
                  onEnviar={(arquivo) => enviarArquivo.mutate({ kind: 'edital', arquivo })}
                  onAbrir={() => void abrirArquivo('edital')}
                />
                <Typography.Text type="secondary">
                  Os arquivos ficam em armazenamento privado: o link de leitura é temporário e
                  gerado a cada abertura.
                </Typography.Text>
              </Space>
            )}
          </Card>

          {!ehNova && prova.data ? (
            <Card size="small" title="Provas para Imprimir" style={{ marginTop: 16 }}>
              <DistribuicaoDaProva prova={prova.data} />
            </Card>
          ) : null}

          {!ehNova && prova.data ? (
            <Card size="small" title="Cadastro Assistido" style={{ marginTop: 16 }}>
              <CadastroAssistido
                examId={prova.data.id}
                temProva={Boolean(prova.data.examFilePath)}
                temGabarito={Boolean(prova.data.answerKeyFilePath)}
              />
            </Card>
          ) : null}
        </Col>
      </Row>
    </Space>
  );
}

function ArquivoDaProva({
  titulo,
  caminho,
  enviando,
  onEnviar,
  onAbrir,
}: {
  titulo: string;
  kind: ExamFileKind;
  caminho: string | null;
  enviando: boolean;
  onEnviar: (arquivo: File) => void;
  onAbrir: () => void;
}) {
  return (
    <Space orientation="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text strong>{titulo}</Typography.Text>
      <Space wrap>
        <Upload
          accept="application/pdf"
          showUploadList={false}
          // O envio é nosso: o Upload só entrega o arquivo escolhido.
          beforeUpload={(arquivo) => {
            onEnviar(arquivo as unknown as File);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} loading={enviando}>
            {caminho ? 'Substituir' : 'Enviar PDF'}
          </Button>
        </Upload>
        {caminho ? (
          <Button type="link" icon={<FilePdfOutlined />} onClick={onAbrir}>
            Abrir
          </Button>
        ) : (
          <Typography.Text type="secondary">Nenhum arquivo enviado</Typography.Text>
        )}
      </Space>
    </Space>
  );
}
